import InfoModal from "@/components/core/modals/InfoModal";
import {
  setSignatoryLevel,
  setSignatoryPattern,
  setSignatoryPhoneIsUserJSE,
  setSignatoryWithCertificate,
} from "@/state/transactions/CurrentTransactionEvents";
import { useCurrentTransaction } from "@/state/transactions/CurrentTransactionHook";
import { Signatory as SignatoryType } from "@/type/core/dashboard/dashboard.types";
import { Member } from "@/type/core/offices/offices.types";
import {
  QcpSignatureLevelAvailable,
  SignatureLevelAvailable,
  TiersSignatureLevelAvailable,
} from "@/type/core/transaction/transaction.types";
import { SIGNATURE_LEVEL, SIGNATURE_LEVEL_TIERS } from "@/utils/Constant";
import { CertificateInformationEnum } from "@/utils/enums/CertificateInformationEnum";
import { SignaturePatternEnum } from "@/utils/enums/signature/signature.pattern.enum";
import { U2CertificateLevelEnum } from "@/utils/enums/U2CertificateLevelEnum";
import { Select } from "antd";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface SignatoryProps {
  signatory: SignatoryType;
  isModelSignatory?: boolean;
  activateSignLevel2: boolean;
  experts: Member[];
  originEditor?: boolean;
}

const Signatory: FunctionComponent<SignatoryProps> = ({
  signatory,
  isModelSignatory = false,
  activateSignLevel2,
  experts,
  originEditor,
}) => {
  const { t } = useTranslation();

  const { transactionForm } = useCurrentTransaction();

  const officeId = transactionForm.office;
  const level = signatory.level;
  const email = signatory.email;
  const certificate = signatory.certificate;
  const canChangeCertificateLevel =
    certificate &&
    certificate === CertificateInformationEnum.VALID_US_CERTIFICATE;

  // Le withCertificate doit être immuable quand on arrive sur la page
  // Le use ref conservera sa valeur initiale sans action sur .current
  const withCertificate = useRef(signatory.withCertificate);

  const getCorrectSignatureLevel = (): SignatureLevelAvailable => {
    if (level) {
      if (level === U2CertificateLevelEnum.QUALIFIED_CERTIFICATE_POLICY) {
        return SignatureLevelAvailable.LEVEL_3;
      } else if (
        level === U2CertificateLevelEnum.LIGHT_CERTIFICATE_POLICY &&
        (certificate === CertificateInformationEnum.QCP_CERTIFICATE ||
          certificate === CertificateInformationEnum.JSE_CERTIFICATE ||
          withCertificate.current)
      ) {
        return SignatureLevelAvailable.LEVEL_2;
      } else {
        return SignatureLevelAvailable.LEVEL_1;
      }
    } else if (certificate != undefined) {
      if (
        certificate === CertificateInformationEnum.QCP_CERTIFICATE ||
        certificate === CertificateInformationEnum.JSE_CERTIFICATE ||
        certificate === CertificateInformationEnum.VALID_US_CERTIFICATE
      ) {
        // On set level_2 par défaut même quand l'utilisateur peut level_3
        return SignatureLevelAvailable.LEVEL_2;
      } else {
        return SignatureLevelAvailable.LEVEL_1;
      }
    } else {
      return SignatureLevelAvailable.LEVEL_1;
    }
  };

  const [signatureLevel, setSignatureLevel] = useState<SignatureLevelAvailable>(
    getCorrectSignatureLevel(),
  );

  useEffect(() => {
    if (!level && certificate) {
      // We don't set withCertificate = true for JSE_CERTIFICATE and QCP_CERTIFICATE as this is only for third signatories (tiers)
      if (certificate === CertificateInformationEnum.VALID_US_CERTIFICATE) {
        setSignatoryWithCertificate({
          email,
          withCertificate: true,
        });
        withCertificate.current = true;
      }
    }
  }, [level, certificate]);

  useEffect(() => {
    const signatoryFromAnotherOffice =
      certificate &&
      certificate === CertificateInformationEnum.JSE_CERTIFICATE &&
      experts &&
      experts.filter(
        (expert) => expert.officeId === officeId && expert.email === email,
      ).length === 0;

    if (signatoryFromAnotherOffice) {
      setSignatoryPhoneIsUserJSE(email);
    }
  }, []);

  const handleChangeTiersLevel = (level: TiersSignatureLevelAvailable) => {
    setSignatoryWithCertificate({
      email,
      withCertificate: level !== SignatureLevelAvailable.LEVEL_1 ? true : null,
    });
    setSignatoryPattern({
      email,
      pattern:
        level === SignatureLevelAvailable.LEVEL_2
          ? SignaturePatternEnum.SIGN_LEVEL_2_EC
          : SignaturePatternEnum.DEFAULT,
    });
    setSignatoryLevel({
      email,
      level: U2CertificateLevelEnum.LIGHT_CERTIFICATE_POLICY,
    });
    setSignatureLevel(level);
  };

  const handleChangeUserWithQCPLevel = (level: QcpSignatureLevelAvailable) => {
    setSignatoryWithCertificate({
      email,
      withCertificate: null,
    });
    setSignatoryPattern({
      email,
      pattern:
        level === SignatureLevelAvailable.LEVEL_3
          ? SignaturePatternEnum.SIGN_LEVEL_QCP_EC
          : SignaturePatternEnum.SIGN_LEVEL_2_EC,
    });
    setSignatoryLevel({
      email,
      level:
        level === SignatureLevelAvailable.LEVEL_3
          ? U2CertificateLevelEnum.QUALIFIED_CERTIFICATE_POLICY
          : U2CertificateLevelEnum.LIGHT_CERTIFICATE_POLICY,
    });
    setSignatureLevel(level);
  };

  return activateSignLevel2 && !isModelSignatory ? (
    <td key="certificate">
      {certificate &&
        certificate === CertificateInformationEnum.QCP_CERTIFICATE &&
        signatureLevel !== SignatureLevelAvailable.LEVEL_1 && (
          <div
            data-test="input-label"
            data-alt="select_signature_level"
            data-test-for="signature_level"
          >
            <Select
              className="select-font-size select-width-350"
              data-test="signature_level"
              onChange={handleChangeUserWithQCPLevel}
              value={signatureLevel}
              popupMatchSelectWidth={false}
              popupClassName="select-font-size-dropdown"
            >
              {SIGNATURE_LEVEL.map((sigLevel, index) => (
                <Select.Option
                  key={sigLevel.level + index}
                  value={sigLevel.level}
                  title={sigLevel.label}
                >
                  {sigLevel.label}
                </Select.Option>
              ))}
            </Select>
            <InfoModal className="ml-8" value="qcpSignature" />
          </div>
        )}
      {certificate &&
        certificate === CertificateInformationEnum.JSE_CERTIFICATE && (
          <div
            className="d-flex justify-content-start ml-16 align-items-center"
            data-cell-alt="jse_certificate"
          >
            <span>
              {t("account.mySignature.certificate.certificateLevelNames.2")}
            </span>
            <InfoModal className="ml-8" value={"jseValidCertificate"} />
          </div>
        )}
      {certificate &&
        certificate === CertificateInformationEnum.PROGRESS_JSE_CERTIFICATE && (
          <div
            className="d-flex justify-content-start ml-16 align-items-center"
            data-cell-alt="jse_member"
          >
            <span>
              {t("account.mySignature.certificate.certificateLevelNames.1")}
            </span>
            <InfoModal className="ml-8" value={"jseMember"} />
          </div>
        )}
      {certificate &&
        certificate === CertificateInformationEnum.REVOKED_JSE_CERTIFICATE && (
          <div
            className="d-flex justify-content-start ml-16 align-items-center"
            data-cell-alt="jse_revoked_certificate"
          >
            <span>
              {t("account.mySignature.certificate.certificateLevelNames.1")}
            </span>
            <InfoModal className="ml-8" value={"jseRevokedCertificate"} />
          </div>
        )}
      {withCertificate.current === false &&
        certificate !== CertificateInformationEnum.QCP_CERTIFICATE && (
          <div
            className="d-flex justify-content-start ml-16 align-items-center"
            data-cell-alt={originEditor ? "withCertificateFalse" : "phoneUsed"}
          >
            <span className="d-block mr-1">
              {t("account.mySignature.certificate.certificateLevelNames.1")}
            </span>
            <InfoModal
              className="ml-8"
              value={originEditor ? "withCertificateFalse" : "phoneUsed"}
            />
          </div>
        )}
      {(canChangeCertificateLevel ||
        (!certificate && withCertificate.current !== false)) &&
        signatureLevel !== SignatureLevelAvailable.LEVEL_3 && (
          <div
            className="d-flex justify-content-flex-start align-items-center"
            data-test="input-label"
            data-alt="select_signature_level"
            data-test-for="signature_level"
          >
            <Select
              className="select-font-size"
              onChange={handleChangeTiersLevel}
              value={signatureLevel}
              data-test="signature_level"
              popupMatchSelectWidth={false}
              popupClassName="select-font-size-dropdown"
            >
              {SIGNATURE_LEVEL_TIERS.map((sigLevel, index) => (
                <Select.Option
                  key={sigLevel.level + index}
                  value={sigLevel.level}
                  title={sigLevel.label}
                  data-test="select-list-option"
                >
                  {sigLevel.label}
                </Select.Option>
              ))}
            </Select>
            <InfoModal className="ml-8" value={"advancedSignature"} />
          </div>
        )}
    </td>
  ) : (
    <></>
  );
};

export default Signatory;
